import React, { useState, useEffect, useRef } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

export default function CustomInput({
  label,
  inputName,
  value = "",
  required = false,
  placeholder = "",
  className = "input-field",
  inputType = "text", // "text", "password", "select", "select-search"
  options = [],
  onChange = null,
  error = "",
  onBlur = () => {},
  readOnly = false,
  isLoading = false,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const dropdownRef = useRef(null);

  // Filter options
  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Click outside to close
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Update selected label
  useEffect(() => {
    const selected = options.find((o) => o.value === value);
    if (selected) setSearchTerm(selected.label);
  }, [value]);

  const handleSelect = (opt) => {
    onChange({ target: { name: inputName, value: opt.value } });
    setShowDropdown(false);
    setSearchTerm(opt.label);
  };

  // Determine actual input type for password toggle
  const resolvedType =
    inputType === "password" ? (showPassword ? "text" : "password") : inputType;

  return (
    <div className="space-y-2 mb-4">
      {label && (
        <label className="block text-sm font-semibold text-gray-700">
          {label}
        </label>
      )}

      <div className="relative" ref={dropdownRef}>
        {/* ---- SEARCHABLE SELECT ---- */}
        {inputType === "select-search" ? (
          <>
            <input
              type="text"
              value={searchTerm}
              onFocus={() => setShowDropdown(true)}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowDropdown(true);
              }}
              placeholder={placeholder || "Search..."}
              className={`input-field w-full pr-10 ${className}`}
            />

            {showDropdown && (
              <div className="absolute top-full left-0 w-full bg-white border border-gray-300 rounded-md max-h-60 overflow-auto shadow-md z-50">
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((opt) => (
                    <div
                      key={opt.value}
                      onClick={() => handleSelect(opt)}
                      className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                    >
                      {opt.label}
                    </div>
                  ))
                ) : (
                  <div className="px-3 py-2 text-gray-500 text-sm">
                    No results found
                  </div>
                )}
              </div>
            )}
          </>
        ) : inputType === "select" ? (
          /* ---- NORMAL SELECT ---- */
          <select
            name={inputName}
            value={value}
            onChange={(e) => onChange(e)}
            className={`input-field w-full pr-10 ${className}`}
            required={required}
            disabled={readOnly}
          >
            <option value="">-- Select --</option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : (
          /* ---- NORMAL INPUT OR PASSWORD INPUT ---- */
          <input
            type={resolvedType}
            name={inputName}
            value={value}
            onInput={(e) => onChange(e)}
            placeholder={placeholder}
            onBlur={onBlur}
            className={`input-field w-full pr-10 ${className}`}
            required={required}
            readOnly={readOnly}
          />
        )}

        {/* ---- PASSWORD EYE TOGGLE ---- */}
        {inputType === "password" && (
          <button
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
            className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
          >
            {showPassword ? (
              <VisibilityOffIcon fontSize="small" />
            ) : (
              <VisibilityIcon fontSize="small" />
            )}
          </button>
        )}

        {/* ---- LOADER ---- */}
        {isLoading && (
          <div className="absolute inset-y-0 right-3 flex items-center">
            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
      </div>

      {/* ---- ERROR MESSAGE ---- */}
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
}
